import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { DateTimeStandard, Box, Button, Card, Stack } from "@curves/ui-base";
import useCyclePurchaseApi from "@apis/useCyclePurchaseApi";
import { Grid } from "@mui/material";
import { parseDate } from "@util/moment";
import { formatCurrencyFn, isEqual, isFunction } from "@util/utils";
import CyclePurchaseDetailOrder from "@icoach/healthyCyclePurchase/CyclePurchaseDetailOrder";
import CyclePurchaseDetailOrderFuture from "@icoach/healthyCyclePurchase/CyclePurchaseDetailOrderFuture";
import { DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER } from "@icoach/router/routerPath";
import PageLayout from "@icoach/components/layout/PageLayout";
import TabsCardLayout from "@icoach/components/layout/TabsCardLayout";
import CycleOrderRecordList from "@icoach/members/components/CycleOrderRecordList";
import CyclePurchaseDialogBox from "@icoach/healthyCyclePurchase/dialog/CyclePurchaseDialogBox";
import { CyclePurchaseDetailOrderActionMenu } from "@icoach/healthyCyclePurchase/components";

const TAB_MAIN = "main";
const TAB_HISTORY = "history";
const TAB_FUTURE = "future";

const cyclePurchaseSummaryInfo = [
	{
		key: "cycleOrderNo",
		text: "主單編號",
	},
	{
		key: "cycleOrderTypeText",
		text: "訂單狀態",
	},
	{
		key: "cycleOrderDate",
		text: "建單日期",
		formatter: value => parseDate(value, DateTimeStandard.DisplayDateBySlash),
	},
	{
		key: "updatedDate",
		text: "更新日期",
		formatter: value => parseDate(value, DateTimeStandard.DisplayDateBySlash),
	},
	{
		key: "shippingDayText",
		text: "出貨週期",
		formatter: value => {
			if (value) return `${value}日`;
			return "";
		},
	},
	{
		key: "memberName",
		text: "訂購人",
	},
	{
		key: "saleAmount",
		text: "訂單金額",
		formatter: value => {
			return formatCurrencyFn(value, { symbol: "$" });
		},
	},
	{
		key: "cardExpiryDate",
		text: "信用卡效期",
		formatter: value => {
			if (value && value.length) {
				const month = value.substring(0, 2);
				const year = value.substring(2, 4);
				return `20${year}年${month}月`;
			} else {
				return value;
			}
		},
	},
	{
		key: "firstShipmentMonth",
		text: "首次出貨月",
		formatter: value => parseDate(value, "YYYY年MM月"),
	},
];

const CyclePurchaseSummaryCard = props => {
	const { payment, ...other } = props.sourceData || {};
	let sourceData = {
		...other,
		...payment,
	};

	const handleValue = (value, formatter) => {
		if (!formatter) return value;

		if (isFunction(formatter)) return formatter(value);
	};

	return (
		<Card className={"mb-3 px-4 pt-3 pb-2"}>
			<Card.CardContent>
				<Grid spacing={2} container>
					{cyclePurchaseSummaryInfo.map((item, i) => {
						const { key, text, formatter } = item;
						return (
							<Grid key={`${key}-${i}`} className={"mb-1"} xs={6} lg={4} xl={3} item>
								<Grid spacing={2} container>
									<Grid xs={5} item>
										<Box className={"font-color-3"}>{text}</Box>
									</Grid>
									<Grid xs={7} item>
										<Box>{handleValue(sourceData[key], formatter)}</Box>
									</Grid>
								</Grid>
							</Grid>
						);
					})}
				</Grid>
			</Card.CardContent>
		</Card>
	);
};

const CyclePurchaseDetailPage = props => {
	const { memberID, cycleOrderID, tabs: activeTab } = props.match.params;
	const { getCyclePurchaseMemberOrderApi } = useCyclePurchaseApi();
	const [sourceData, setSourceData] = useState({});

	const dialogRef = useRef(null);

	const doInitCyclePurchaseApi = useCallback(
		async cycleOrderID => {
			let res = await getCyclePurchaseMemberOrderApi(cycleOrderID);
			if (res) {
				setSourceData(res);
			}
		},
		// eslint-disable-next-line
		[],
	);

	const refreshFn = useCallback(
		() => {
			doInitCyclePurchaseApi(cycleOrderID);
		},
		// eslint-disable-next-line
		[cycleOrderID, doInitCyclePurchaseApi],
	);

	const tabData = useMemo(
		() => {
			const { effectionQuantity = 0 } = sourceData || {};
			const LabelComponent = params => {
				const { label, qty = 0 } = params;

				return (
					<React.Fragment>
						<span className={"tab-label"}>{label}</span>
						{qty > 0 && <span className={"tab-label-qty"}>{qty}</span>}
					</React.Fragment>
				);
			};

			return [
				{
					label: "訂單主檔",
					value: TAB_MAIN,
					component: Link,
					to: DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER.DETAIL.GET_MAIN_PATH(memberID, cycleOrderID),
				},
				{
					label: "訂單歷史",
					value: TAB_HISTORY,
					component: Link,
					to: DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER.DETAIL.GET_HISTORY_PATH(memberID, cycleOrderID),
				},
				{
					label: <LabelComponent label={"預計變更"} qty={effectionQuantity} />,
					value: TAB_FUTURE,
					component: Link,
					to: DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER.DETAIL.GET_FUTURE_PATH(memberID, cycleOrderID),
				},
			];
		},
		// eslint-disable-next-line
		[JSON.stringify(sourceData), memberID, cycleOrderID],
	);

	useEffect(() => {
		if (cycleOrderID) {
			doInitCyclePurchaseApi(cycleOrderID);
		}
		// eslint-disable-next-line
	}, [cycleOrderID]);

	return (
		<PageLayout>
			<PageLayout.Header title="健康產品循環訂購單">
				<Stack className={"ml-auto"} spacing={2}>
					<Button as={Link} variant="contained" to={DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER.BASE}>
						返回總覽
					</Button>
					<CyclePurchaseDetailOrderActionMenu sourceData={sourceData} refresh={refreshFn} dialogRef={dialogRef} />
				</Stack>
			</PageLayout.Header>
			<CyclePurchaseSummaryCard sourceData={sourceData} />
			<TabsCardLayout tabs={tabData} activeTab={activeTab} tabsContent={{ className: "py-3 px-4" }}>
				{isEqual(activeTab, TAB_MAIN) && <CyclePurchaseDetailOrder sourceData={sourceData} />}
				{isEqual(activeTab, TAB_HISTORY) && <CycleOrderRecordList sourceData={sourceData} />}
				{isEqual(activeTab, TAB_FUTURE) && <CyclePurchaseDetailOrderFuture sourceData={sourceData} refresh={refreshFn} dialogRef={dialogRef} />}
			</TabsCardLayout>
			<CyclePurchaseDialogBox ref={dialogRef} />
		</PageLayout>
	);
};

export default CyclePurchaseDetailPage;
